import { useState, useEffect, useMemo } from 'react'
import type { NextPage, GetStaticPropsContext } from 'next'
import { Box, Progress, Text } from '@chakra-ui/react'

import { ContainerBase } from '@/components/containers/ContainerBase'
import { useBlogCms } from '@/hooks/useBlogCms'
import { TagsHeader } from '@/features/education/TagsHeader'
import { HomePage } from '@/features/education/HomePage'
import { fetchEducationPage } from '@/utils/fetchEducationPage'

const EducationHomePage: NextPage = () => {
  const [featuredVideo, setFeaturedVideo] = useState('')
  const {
    compassContent: featuredPosts,
    loading: featuredPostsLoading,
    getContent: getFeaturedPosts,
  } = useBlogCms()
  const {
    compassContent: beginnerPosts,
    loading: beginnerPostsLoading,
    getContent: getBeginnerPosts,
  } = useBlogCms()
  const {
    compassContent: advancedPosts,
    loading: advancedPostsLoading,
    getContent: getAdvancedPosts,
  } = useBlogCms()
  const {
    compassContent: institutionalPosts,
    loading: institutionalPostsLoading,
    getContent: getInstitutionalPosts,
  } = useBlogCms()
  const {
    compassContent: researchPosts,
    loading: researchPostsLoading,
    getContent: getResearchPosts,
  } = useBlogCms()
  const {
    compassContent: downloadsPosts,
    loading: downloadsPostsLoading,
    getContent: getDownloadsPosts,
  } = useBlogCms()

  const getFeaturedVideo = async () => {
    const { html } = await fetchEducationPage('featured-video')
    setFeaturedVideo(html)
  }

  const getParamsByTag = (tag = '') => {
    return `&page=1&limit=3&include=tags,authors&order=published_at%20desc&filter=tags:${tag}`
  }

  useEffect(() => {
    getFeaturedVideo()

    const abortController = new AbortController()
    getFeaturedPosts(
      {
        path: '/content/posts',
        params:
          '&page=1&limit=1&include=tags,authors&order=published_at%20desc&filter=featured:true',
      },
      abortController
    )
    getBeginnerPosts(
      {
        path: '/content/posts',
        params: getParamsByTag('beginner'),
      },
      abortController
    )
    getAdvancedPosts(
      {
        path: '/content/posts',
        params: getParamsByTag('advanced'),
      },
      abortController
    )
    getInstitutionalPosts(
      {
        path: '/content/posts',
        params: getParamsByTag('institutional'),
      },
      abortController
    )
    getResearchPosts(
      {
        path: '/content/posts',
        params: getParamsByTag('research'),
      },
      abortController
    )
    getDownloadsPosts(
      {
        path: '/content/posts',
        params: getParamsByTag('downloads'),
      },
      abortController
    )

    return () => {
      abortController.abort()
    }
  }, [])

  const arePostsLoading = useMemo(() => {
    return (
      featuredPostsLoading ||
      beginnerPostsLoading ||
      advancedPostsLoading ||
      institutionalPostsLoading ||
      researchPostsLoading ||
      downloadsPostsLoading
    )
  }, [
    featuredPostsLoading,
    beginnerPostsLoading,
    advancedPostsLoading,
    institutionalPostsLoading,
    researchPostsLoading,
    downloadsPostsLoading,
  ])

  return (
    <>
      <Box bg="black" borderBottomWidth={1} borderColor="gray.930">
        <ContainerBase py={6} maxWidth={{ base: '100%', xl: '1280px' }}>
          <Text
            as="h1"
            fontSize="40px"
            fontWeight="400"
            lineHeight="normal"
            variant="spaceGrotesk"
            mb={3}
            color="white"
          >
            Content
          </Text>
          <TagsHeader />
        </ContainerBase>
      </Box>
      {arePostsLoading ? (
        <ContainerBase py="50px" maxWidth={{ base: '100%', xl: '1280px' }}>
          <Progress size="xs" isIndeterminate colorScheme="brand" />
        </ContainerBase>
      ) : (
        <HomePage
          featuredVideo={featuredVideo}
          featuredPosts={featuredPosts}
          beginnerPosts={beginnerPosts}
          advancedPosts={advancedPosts}
          institutionalPosts={institutionalPosts}
          researchPosts={researchPosts}
          downloadsPosts={downloadsPosts}
        />
      )}
    </>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
      revalidate: 60,
      htmlClass: 'education',
      seo: {
        title: 'Compass Mining Content',
        breadcrumbTitle: 'Compass Mining Content',
      },
    },
  }
}

export default EducationHomePage
